import { useEffect, useRef, useState, useCallback } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function ContactSection() {
  const [bgOffset, setBgOffset] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  const rafRef = useRef<number>(0);
  const { ref: formRef, isVisible: formVisible } = useScrollReveal({ threshold: 0.15 });
  const { ref: imageRef, isVisible: imageVisible } = useScrollReveal({ threshold: 0.15 });

  const handleScroll = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      const el = sectionRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const height = el.offsetHeight;
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          const progress = Math.max(0, -rect.top + window.innerHeight) / (height + window.innerHeight);
          setBgOffset(progress * 60);
        }
      }
      rafRef.current = 0;
    });
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [handleScroll]);

  return (
    <section id="contact" className="relative py-24 md:py-36 bg-background-950" ref={sectionRef}>
      <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left: text */}
          <div
            ref={formRef}
            className={`lg:w-1/2 transition-all duration-1000 ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-6">
              Put intelligence
              <br />
              at the <span className="text-primary-400">forefront</span>
            </h2>
            <p className="text-white/50 text-base md:text-lg leading-relaxed mb-10 max-w-md">
              Where it drives everything else. One system, one team. Tell us what you are building.&nbsp;
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-primary-400" />
                </div>
                <div>
                  <p className="text-sm text-white/40 mb-1">Email us</p>
                  <a
                    href="mailto:hello@ainnovation.now"
                    className="text-white hover:text-primary-400 transition-colors duration-200 cursor-pointer"
                    rel="nofollow"
                  >
                    hello@ainnovation.now
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right: image box */}
          <div
            ref={imageRef}
            className={`lg:w-1/2 transition-all duration-1000 delay-200 ${imageVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="relative rounded-2xl overflow-hidden border border-white/5 aspect-[4/3] will-change-transform">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20visualization%20of%20customer%20service%20and%20contact%20center%20theme%2C%20glowing%20interconnected%20conversation%20nodes%20and%20floating%20message%20bubbles%20in%20deep%20dark%20space%2C%20warm%20human-centric%20technology%20aesthetic%20with%20soft%20copper%20and%20amber%20light%20trails%2C%20geometric%20communication%20network%20patterns%20radiating%20from%20a%20central%20hub%2C%20ethereal%20particle%20streams%20representing%20real-time%20connections%2C%20minimalist%20futuristic%20composition%2C%20serene%20professional%20atmosphere%2C%20high-end%20editorial%20design%2C%20smooth%20gradient%20depth%20with%20subtle%20glowing%20accents&width=900&height=675&seq=contact-cs-image-v3&orientation=landscape&nocache=true"
                alt="Abstract customer service and contact center visualization - interconnected communication nodes"
                className="w-full h-full object-cover object-center animate-breathe-image"
                style={{ transform: `translateY(${-bgOffset}px)` }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950/40 to-transparent" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}